// WhatsApp API Service
// In production, this would connect to your actual WhatsApp Business API
// For now, this is a mock implementation

const WHATSAPP_API_URL = process.env.REACT_APP_WHATSAPP_API_URL || 'http://localhost:4000/api/send-whatsapp';

/**
 * Fetches an access token using app ID and app secret
 * @param {string} appId - Facebook App ID
 * @param {string} appSecret - Facebook App Secret
 * @returns {Promise<{success: boolean, access_token?: string, error?: string}>}
 */
export const getAccessToken = async (appId, appSecret) => {
  try {
    // Validate inputs
    if (!appId || !appSecret) {
      return {
        success: false,
        error: 'App ID and App Secret are required',
      };
    }

    // Facebook Graph API OAuth endpoint
    const url = `https://graph.facebook.com/oauth/access_token?client_id=${appId}&client_secret=${appSecret}&grant_type=client_credentials`;

    const response = await fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to fetch access token';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error?.message || errorMessage;
      } catch {
        // If response is not JSON, use the text as error message
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    // Facebook Graph API returns form-encoded data, not JSON
    const responseText = await response.text();
    const params = new URLSearchParams(responseText);
    const accessToken = params.get('access_token');

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    return {
      success: true,
      access_token: accessToken,
    };
  } catch (error) {
    console.error('Error fetching access token:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch access token. Please try again later.',
    };
  }
};

/**
 * Sends OTP to the provided phone number via WhatsApp
 * @param {string} phoneNumber - Phone number in international format (e.g., +1234567890)
 * @returns {Promise<{success: boolean, message: string, otp?: string}>}
 */
export const sendOTP = async (phoneNumber) => {
  console.log("inside send otp", phoneNumber);
  try {
    // Validate phone number format
    if (!phoneNumber || !phoneNumber.match(/^\+?[1-9]\d{1,14}$/)) {
      return {
        success: false,
        message: 'Invalid phone number format. Please use international format (e.g., 91234567890)',
      };
    }

    // Generate a 6-digit OTP
    const otp = Math.floor(100000 + Math.random() * 900000).toString();

    // Route through local proxy server to avoid CORS (browser cannot call Facebook API directly)
    const proxyUrl = process.env.REACT_APP_WHATSAPP_API_URL || 'http://localhost:4000/api/send-whatsapp';
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: phoneNumber,
        otp
      }),
    });

    console.log("response", response);
    if (!response.ok) {
      throw new Error('Failed to send OTP');
    }
   

    // For development/demo purposes, we'll simulate the API call
    // In production, remove this and use the actual API call above
    console.log(`[MOCK] Sending OTP ${otp} to ${phoneNumber} via WhatsApp`);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Store OTP in sessionStorage for verification (in production, this would be handled server-side)
    sessionStorage.setItem('pendingOTP', otp);
    sessionStorage.setItem('pendingPhone', phoneNumber);
    sessionStorage.setItem('otpExpiry', Date.now() + 600000); // 10 minutes

    return {
      success: true,
      message: `OTP sent successfully to ${phoneNumber}`,
      otp: process.env.NODE_ENV === 'development' ? otp : undefined, // Only return OTP in dev mode
    };
  } catch (error) {
    console.error('Error sending OTP:', error);
    return {
      success: false,
      message: 'Failed to send OTP. Please try again later.',
    };
  }
};

/**
 * Verifies the OTP entered by the user
 * @param {string} phoneNumber - Phone number
 * @param {string} enteredOTP - OTP entered by user
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const verifyOTP = async (phoneNumber, enteredOTP) => {
  try {
    const storedOTP = sessionStorage.getItem('pendingOTP');
    const storedPhone = sessionStorage.getItem('pendingPhone');
    const expiryTime = sessionStorage.getItem('otpExpiry');

    // Check if OTP exists
    if (!storedOTP || !storedPhone) {
      return {
        success: false,
        message: 'No OTP found. Please request a new OTP.',
      };
    }

    // Check if phone number matches
    if (storedPhone !== phoneNumber) {
      return {
        success: false,
        message: 'Phone number mismatch. Please request a new OTP.',
      };
    }

    // Check if OTP has expired
    if (Date.now() > parseInt(expiryTime)) {
      sessionStorage.removeItem('pendingOTP');
      sessionStorage.removeItem('pendingPhone');
      sessionStorage.removeItem('otpExpiry');
      return {
        success: false,
        message: 'OTP has expired. Please request a new OTP.',
      };
    }

    // Verify OTP
    if (storedOTP !== enteredOTP) {
      return {
        success: false,
        message: 'Invalid OTP. Please try again.',
      };
    }

    // OTP verified successfully
    sessionStorage.removeItem('pendingOTP');
    sessionStorage.removeItem('pendingPhone');
    sessionStorage.removeItem('otpExpiry');

    return {
      success: true,
      message: 'OTP verified successfully!',
    };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return {
      success: false,
      message: 'Failed to verify OTP. Please try again.',
    };
  }
};

